'use client'

import React from 'react'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { AlertTriangle } from 'lucide-react'

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-32 left-1/3 h-80 w-80 rounded-full bg-rose-500/15 blur-3xl" />
            <div className="absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-orange-500/10 blur-3xl" />
          </div>

          <GlassCard tone="red" className="w-full max-w-md p-6 md:p-8 text-center">
            <div className="mx-auto mb-4 inline-flex rounded-2xl bg-rose-500/15 ring-1 ring-inset ring-rose-500/30 p-3">
              <AlertTriangle className="h-8 w-8 text-rose-300" />
            </div>
            <h2 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-white/90 mb-2">
              Something went wrong
            </h2>
            <p className="text-sm text-neutral-600 dark:text-white/60 mb-6">
              {this.state.error?.message || 'An unexpected error occurred while loading this page.'}
            </p>
            <Button
              onClick={this.handleReset}
              className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold shadow-lg shadow-orange-500/30 border-0"
            >
              Try Again
            </Button>
          </GlassCard>
        </div>
      )
    }

    return this.props.children
  }
}

// Wrapper function for easier usage
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
) {
  return function WrappedComponent(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}
